import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { FileText, TrendingUp, TrendingDown, Scale, DollarSign } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { 
    style: 'currency', 
    currency: 'EUR' 
  }).format(value);
};

const ReportSection = ({ title, accounts, total, color }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <h3 className="font-heading font-semibold text-foreground">{title}</h3>
      <Badge variant="outline" className={color}>
        {formatCurrency(total)}
      </Badge>
    </div>
    <div className="space-y-1">
      {accounts.map(acc => (
        <div 
          key={acc.id}
          className="flex items-center justify-between p-2 rounded-lg bg-background/50 text-sm"
        >
          <div className="flex items-center gap-3">
            <span className="font-mono text-xs text-primary w-12">{acc.code}</span>
            <span className="text-muted-foreground">{acc.name}</span>
          </div>
          <span className="font-mono text-foreground">{formatCurrency(acc.balance)}</span>
        </div>
      ))}
      {accounts.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-2">Nessun conto</p>
      )}
    </div>
  </div>
);

const ReportsPage = () => {
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('balance-sheet');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [bsRes, isRes] = await Promise.all([
        axios.get(`${API}/reports/balance-sheet`),
        axios.get(`${API}/reports/income-statement`)
      ]);
      setBalanceSheet(bsRes.data);
      setIncomeStatement(isRes.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="reports-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Report</h1>
          <p className="text-muted-foreground mt-1">Analisi finanziaria aziendale</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-card border border-border p-1">
          <TabsTrigger 
            value="balance-sheet" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            data-testid="tab-balance-sheet"
          >
            <Scale size={16} className="mr-2" />
            Stato Patrimoniale
          </TabsTrigger>
          <TabsTrigger 
            value="income-statement"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
            data-testid="tab-income-statement"
          >
            <TrendingUp size={16} className="mr-2" />
            Conto Economico
          </TabsTrigger>
        </TabsList>

        {/* Balance Sheet */}
        <TabsContent value="balance-sheet" className="space-y-6" data-testid="balance-sheet-content">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Totale Attività</p>
                    <p className="text-2xl font-heading font-bold text-foreground">
                      {formatCurrency(balanceSheet?.totals?.assets || 0)}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-green-500/10">
                    <TrendingUp size={24} className="text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Totale Passività</p>
                    <p className="text-2xl font-heading font-bold text-foreground">
                      {formatCurrency(balanceSheet?.totals?.liabilities || 0)}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-red-500/10">
                    <TrendingDown size={24} className="text-red-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Patrimonio Netto</p>
                    <p className="text-2xl font-heading font-bold text-primary">
                      {formatCurrency(balanceSheet?.totals?.equity || 0)}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-primary/10">
                    <DollarSign size={24} className="text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Balance Sheet Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Attività</CardTitle>
                <CardDescription>Beni e diritti dell'azienda</CardDescription>
              </CardHeader>
              <CardContent>
                <ReportSection 
                  title="Dettaglio Attività"
                  accounts={balanceSheet?.assets || []}
                  total={balanceSheet?.totals?.assets || 0}
                  color="bg-green-500/20 text-green-400 border-green-500/30"
                />
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Passività e Patrimonio</CardTitle>
                <CardDescription>Debiti e capitale proprio</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <ReportSection 
                  title="Passività"
                  accounts={balanceSheet?.liabilities || []}
                  total={balanceSheet?.totals?.liabilities || 0}
                  color="bg-red-500/20 text-red-400 border-red-500/30"
                />
                <ReportSection 
                  title="Patrimonio Netto"
                  accounts={balanceSheet?.equity || []}
                  total={balanceSheet?.totals?.equity || 0}
                  color="bg-blue-500/20 text-blue-400 border-blue-500/30"
                />
              </CardContent>
            </Card>
          </div>

          {/* Balance Check */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Verifica quadratura (Attivo - Passivo - PN)</span>
                <Badge 
                  variant="outline"
                  className={Math.abs(balanceSheet?.totals?.balance_check || 0) < 0.01 
                    ? "bg-green-500/20 text-green-400 border-green-500/30"
                    : "bg-red-500/20 text-red-400 border-red-500/30"
                  }
                >
                  {formatCurrency(balanceSheet?.totals?.balance_check || 0)}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Income Statement */}
        <TabsContent value="income-statement" className="space-y-6" data-testid="income-statement-content">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Totale Ricavi</p>
                    <p className="text-2xl font-heading font-bold text-foreground">
                      {formatCurrency(incomeStatement?.totals?.revenues || 0)}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-primary/10">
                    <TrendingUp size={24} className="text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Totale Costi</p>
                    <p className="text-2xl font-heading font-bold text-foreground">
                      {formatCurrency(incomeStatement?.totals?.expenses || 0)}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-orange-500/10">
                    <TrendingDown size={24} className="text-orange-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Utile Netto</p>
                    <p className={`text-2xl font-heading font-bold ${
                      (incomeStatement?.totals?.net_income || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {formatCurrency(incomeStatement?.totals?.net_income || 0)}
                    </p>
                  </div>
                  <div className={`p-3 rounded-xl ${
                    (incomeStatement?.totals?.net_income || 0) >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'
                  }`}>
                    <DollarSign size={24} className={
                      (incomeStatement?.totals?.net_income || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                    } />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Income Statement Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Ricavi</CardTitle>
                <CardDescription>Entrate operative dell'azienda</CardDescription>
              </CardHeader>
              <CardContent>
                <ReportSection 
                  title="Dettaglio Ricavi"
                  accounts={incomeStatement?.revenues || []}
                  total={incomeStatement?.totals?.revenues || 0}
                  color="bg-primary/20 text-primary border-primary/30"
                />
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Costi</CardTitle>
                <CardDescription>Spese operative dell'azienda</CardDescription>
              </CardHeader>
              <CardContent>
                <ReportSection 
                  title="Dettaglio Costi"
                  accounts={incomeStatement?.expenses || []}
                  total={incomeStatement?.totals?.expenses || 0}
                  color="bg-orange-500/20 text-orange-400 border-orange-500/30"
                />
              </CardContent>
            </Card>
          </div>

          {/* Result Card */}
          <Card className={`border-2 ${
            (incomeStatement?.totals?.net_income || 0) >= 0 
              ? 'bg-green-500/5 border-green-500/30' 
              : 'bg-red-500/5 border-red-500/30'
          }`}>
            <CardContent className="p-6 text-center">
              <p className="text-sm text-muted-foreground mb-2">Risultato d'Esercizio</p>
              <p className={`text-4xl font-heading font-bold ${
                (incomeStatement?.totals?.net_income || 0) >= 0 ? 'text-green-400' : 'text-red-400'
              }`}>
                {(incomeStatement?.totals?.net_income || 0) >= 0 ? '+' : ''}
                {formatCurrency(incomeStatement?.totals?.net_income || 0)}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {(incomeStatement?.totals?.net_income || 0) >= 0 ? 'Utile' : 'Perdita'} del periodo
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
